/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement, useCallback, useMemo, useState } from 'react';
import { useLocation, useNavigation } from 'react-router';
import { useNavigate } from '@/hooks/use-navigate';

import type { ShopperSearch } from '@/scapi';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion';
import { Checkbox } from '@/components/ui/checkbox';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { UITarget } from '@/targets/ui-target';
import type { FilterValue, RefinementProps } from './types';
import RefineDefault from './refine-default';
import RefineColor from './refine-color';
import RefineSize from './refine-size';
import RefinePrice from './refine-price';

/**
 * The filter rail's group list, per the SRAM **Filters** pattern (Figma `6952:46600`,
 * instantiated on the PLP frame `1396:10895`). CRCB2B-348.
 *
 * Presentational with respect to layout: the caller decides whether this renders in the desktop
 * sidebar or inside the overlay below `lg`, and passes `idPrefix` so two mounted copies cannot
 * collide on generated ids.
 */
export default function CategoryRefinements({
    result,
    refine = [],
    idPrefix,
}: {
    result: ShopperSearch.schemas['ProductSearchResult'];
    refine: string[];
    /** Namespaces generated ids. See `RefinementProps.idPrefix`. */
    idPrefix?: string;
}): ReactElement {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const navigation = useNavigation();
    const isPending = navigation.state !== 'idle';

    /**
     * Optimistic refinements derived from the in-flight navigation target.
     *
     * When a user toggles a filter, `navigate()` updates the URL which triggers a loader call. While that navigation
     * is pending, `navigation.location` holds the target location, allowing us to read the intended refine params
     * immediately.
     *
     * Note: `navigation.location` is inherently tied to the pending state — it's only defined while
     * `navigation.state !== 'idle'`, and both reset in the same render cycle once the navigation completes. An
     * additional `isPending` guard is therefore not needed here.
     *
     * Minor trade-off: `useNavigation()` is global — it reflects any in-flight navigation, not just filter toggles.
     * In practice this is acceptable because if a different navigation starts, the entire page is about to change
     * anyway.
     */
    const effectiveRefines = navigation.location
        ? new URLSearchParams(navigation.location.search).getAll('refine')
        : refine;

    const hasActiveFilter = useCallback(
        (attributeId: string) => {
            return effectiveRefines.some((r) => r.startsWith(`${attributeId}=`));
        },
        [effectiveRefines]
    );

    /** How many of a group's values are currently applied. Drives the header count. */
    const activeFilterCount = useCallback(
        (attributeId: string) => {
            return effectiveRefines.filter((r) => r.startsWith(`${attributeId}=`)).length;
        },
        [effectiveRefines]
    );

    /**
     * `cgid` is excluded: **category selection is navigation here, not filtering.** The nav menu
     * and breadcrumbs own it, which is why the PLP design shows no Category group in the rail.
     *
     * It is also unusable as configured. The catalogue defines the `type="category"` refinement on
     * **`root` only**, so it returns root's children — a single `Shop` value — on every PLP
     * regardless of depth, identical on `…/mountain` and `…/handlebars` alike. A lone value that
     * matches everything already on screen cannot narrow anything.
     *
     * CRCB2B-348 briefly rendered it, on a misreading of the AC: its global-filter list names
     * "Category-Aware Filters", which points at the category-SPECIFIC attribute section (Wheels →
     * Hub Width, Brakes → Rotor Size) rather than asking for a filter called Category.
     *
     * If category filtering is ever wanted, the configuration has to change first — per-category
     * refinement definitions so each PLP lists its own children. Recorded for the BM phase.
     */
    const refinements = useMemo(
        () => (result?.refinements || []).filter((refinement) => refinement.attributeId !== 'cgid'),
        [result]
    );

    /**
     * Which groups a user has explicitly opened or closed, by attribute id.
     *
     * The open set is `override ?? hasActiveFilter`, so a group holding applied filters starts
     * open and an incoming filter expands its group — while a group the user opened or closed by
     * hand keeps that state. Recording closes as well as opens is what lets a user collapse a
     * group that has active filters; keying only off `hasActiveFilter` would spring it back open
     * on the next render.
     *
     * No reset is needed when the refinements change. A filter can only be applied to a group the
     * user has opened, so the one case an override could wrongly suppress — a filter arriving on a
     * group the user explicitly closed — is not reachable through the UI.
     */
    const [openOverrides, setOpenOverrides] = useState<Record<string, boolean>>({});

    const openGroups = useMemo(
        () =>
            refinements
                .map((r) => r.attributeId)
                .filter((attributeId): attributeId is string =>
                    Boolean(attributeId && (openOverrides[attributeId] ?? hasActiveFilter(attributeId)))
                ),
        [refinements, openOverrides, hasActiveFilter]
    );

    const handleOpenChange = useCallback(
        (next: string[]) => {
            setOpenOverrides(
                Object.fromEntries(
                    refinements
                        .map((r) => r.attributeId)
                        .filter((id): id is string => Boolean(id))
                        .map((id) => [id, next.includes(id)])
                )
            );
        },
        [refinements]
    );

    const toggleFilter = useCallback(
        (attributeId: string, value: string) => {
            const params = new URLSearchParams(location.search);
            const refines = params.getAll('refine');
            const refinePair = `${attributeId}=${value}`;

            let nextRefines: string[];
            if (refines.includes(refinePair)) {
                // Remove this refinement
                nextRefines = refines.filter((r) => r !== refinePair);
            } else {
                // Exclusive refinements - only one value can be selected at a time
                const exclusiveRefinements = [
                    'price',
                    // @sfdc-extension-line SFDC_EXT_BOPIS
                    'ilids',
                ];
                if (exclusiveRefinements.includes(attributeId)) {
                    // Remove all refinements for this attribute first
                    nextRefines = [...refines.filter((r) => !r.startsWith(`${attributeId}=`)), refinePair];
                } else {
                    nextRefines = [...refines, refinePair];
                }
            }

            // Rebuild search params with the new refines
            params.delete('refine');
            nextRefines.forEach((r) => params.append('refine', r));
            params.set('offset', '0');

            const nextSearch = `?${params.toString()}`;

            void navigate(
                {
                    pathname: location.pathname,
                    search: nextSearch,
                },
                // Applying a filter is a real navigation — the refinement lives in the URL — and
                // React Router scrolls to the top of every new location while <ScrollRestoration>
                // is mounted (root.tsx). Without this, a shopper who scrolled down the rail to
                // reach a group was thrown back to the breadcrumbs by their own click, and had to
                // scroll back to tick a second value in the group they were already in.
                //
                // Same reasoning as `use-filters-panel-state.ts`, which passes it so that OPENING
                // the panel holds position; this is the same fix for using what the panel holds.
                { preventScrollReset: true }
            );
        },
        [location, navigate]
    );

    // Check if a filter value is selected (uses optimistic state)
    const isFilterSelected = useCallback(
        (attributeId: string, value: string) => {
            return effectiveRefines.includes(`${attributeId}=${value}`);
        },
        [effectiveRefines]
    );

    // Render the appropriate filter component based on type.
    //
    // A BOOLEAN refinement would be selected here too, rendering `FilterBooleanRow` instead of a
    // group. Nothing is wired to it: the design's `New Products` and `Sale` rows have no data
    // source — CRCB2B-385 delivered 615 attribute definitions and none of them is a new/sale
    // flag, and SFCC has no built-in on-sale refinement. SCAPI also does not mark a refinement as
    // boolean, so recognising one needs a convention that does not exist yet. The row type is
    // built and covered in Storybook; this is the point that will select it. (CRCB2B-348 task 5.3.)
    const renderFilterValues = (
        refinement: ShopperSearch.schemas['ProductSearchRefinement'] & { values: FilterValue[] }
    ) => {
        const { attributeId, values, label } = refinement;
        const refinementProps: RefinementProps = {
            values,
            attributeId,
            isFilterSelected,
            toggleFilter,
            idPrefix,
            groupLabel: label || attributeId,
        };

        switch (attributeId) {
            case 'c_refinementColor':
                return <RefineColor {...refinementProps} />;
            case 'c_size':
                return <RefineSize {...refinementProps} />;
            case 'price':
                return <RefinePrice {...refinementProps} result={result} />;
            default:
                return <RefineDefault {...refinementProps} />;
        }
    };

    // No refinements available
    if (refinements.length === 0) {
        return (
            <div className="border p-4">
                <p className="text-muted-foreground text-sm">{t('categoryRefinements:noFilterOptionsAvailable')}</p>
            </div>
        );
    }

    return (
        <UITarget targetId="sfcc.plp.search.filters">
            <div className={isPending ? 'pointer-events-none opacity-50 transition-opacity' : ''}>
                {/*  @sfdc-extension-block-start SFDC_EXT_BOPIS */}
                {/*
                 * BOPIS's "Shop by Availability" filter is deliberately NOT rendered here
                 * (CRCB2B-348). Buy Online / Pickup In Store is a B2C concept — a shopper
                 * reserving goods to collect from a shop — and it is out of scope for this B2B
                 * dealer storefront, where accounts order for delivery. It appears in no SRAM
                 * Figma frame, and it was the one group in this rail still carrying the old
                 * bordered-card treatment.
                 *
                 * The component and its markers are left intact: BOPIS has no runtime switch, and
                 * the supported way to remove it is the uninstall that strips these markers across
                 * ~150 files, reaching add-to-cart, inventory validation and checkout. That needs
                 * its own ticket and QA across those flows. Deleting only this render keeps the
                 * storefront correct in the meantime without half-uninstalling the extension.
                 *
                 * Note this removes BOPIS's PLP filtering only. The store-locator drawer, PDP
                 * availability, cart pickup lines and the checkout pickup option all still render.
                 * TODO(BOPIS-removal): complete the uninstall, or gate the remaining surfaces.
                 */}
                {/*  @sfdc-extension-block-end SFDC_EXT_BOPIS */}

                <Accordion type="multiple" treatment="flush" value={openGroups} onValueChange={handleOpenChange}>
                    {refinements.map((refinement) => {
                        const { values, attributeId, label } = refinement;
                        if (!attributeId || !Array.isArray(values) || !values.length) {
                            return null;
                        }
                        const count = activeFilterCount(attributeId);

                        return (
                            <AccordionItem key={attributeId} value={attributeId} variant="transparent">
                                <AccordionTrigger
                                    className={cn(
                                        // The Filters pattern's header, which differs from the
                                        // Accordion page's on three values: 16px regular rather
                                        // than 18px bold, 8px horizontal padding rather than 24px,
                                        // and a 32px indicator rather than 48px. The vertical 24s
                                        // and that indicator are what make the collapsed group
                                        // 80px instead of 96px. They live here rather than in the
                                        // `flush` treatment because they are this pattern's type
                                        // ramp, not a property of being flush.
                                        //
                                        // Figma specifies no horizontal padding — it puts the 16px
                                        // inset on the CONTAINER, so the host supplies the gutter
                                        // (`DialogBody`'s `p-4`, the sidebar's `px-4`). `px-2`
                                        // deviates by 8px so the focus ring below clears the
                                        // label: at zero the inset outline is drawn through the
                                        // first letter, and no offset fixes it — outward is
                                        // clipped by `AccordionItem`'s `overflow-hidden`, inward
                                        // lands on the text.
                                        'px-2 py-6 text-base leading-5 font-normal',
                                        '[&>[data-slot=accordion-indicator]]:size-8',
                                        // `focus-visible:outline-solid` looks redundant and is
                                        // not: Tailwind v4's `outline-none` sets
                                        // `--tw-outline-style: none`, which `outline-2` resolves
                                        // its style from, so the primitive's pair computes to
                                        // `outline-style: none`. Retired by
                                        // `openspec/changes/focus-visible-indicators` task 5.1.
                                        'focus-visible:outline-solid'
                                    )}>
                                    <span className="flex items-center gap-1">
                                        {label || attributeId}
                                        {count > 0 && (
                                            <span
                                                // The applied-filter count Tyler Trepton approved
                                                // on 2026-07-30, in the form he wrote it:
                                                // `Eye To Eye / Stroke (2)`. There is no design
                                                // for it — the hidden `Supporting Text` node in
                                                // Figma is the pattern's "With Helper" line, not a
                                                // count slot, so that stays free.
                                                data-slot="filter-group-count">
                                                {t('categoryRefinements:activeFilterCount', { count })}
                                            </span>
                                        )}
                                    </span>
                                </AccordionTrigger>
                                <AccordionContent className="px-0 pb-6">
                                    {renderFilterValues(
                                        refinement as ShopperSearch.schemas['ProductSearchRefinement'] & {
                                            values: FilterValue[];
                                        }
                                    )}
                                </AccordionContent>
                            </AccordionItem>
                        );
                    })}
                </Accordion>
            </div>
        </UITarget>
    );
}

/**
 * `Filter/Bar`'s boolean row — a single on/off filter with no disclosure, per Figma's 72px
 * header-only `Filter Accordion` variant. Label leading, 24px `Checkbox` trailing, hairline below.
 *
 * The control is the standardized `Checkbox` from CRCB2B-634, which `selection-controls` already
 * requires filters to compose — so this implements an existing requirement rather than changing
 * one. Unlike a value row, the control here IS drawn, so its 24px geometry applies.
 *
 * Exported for Storybook coverage. **Not yet rendered by the panel**: the design's `New Products`
 * and `Sale` rows have no data source (see the note in `renderFilterValues`).
 *
 * TODO: this is unreachable production code — Storybook is its only consumer. It is kept
 * rather than deleted because the row itself is finished and the gap is upstream: SCAPI does not
 * mark a refinement as boolean, so recognising one needs a convention that does not exist yet.
 * Raise the ticket that either lands that convention (and wires the `switch` in
 * `renderFilterValues` to select this) or removes the row, and put its id above.
 */
export function FilterBooleanRow({
    id,
    label,
    checked,
    onCheckedChange,
    className,
}: {
    id: string;
    label: string;
    checked: boolean;
    onCheckedChange: (checked: boolean) => void;
    className?: string;
}): ReactElement {
    return (
        <div className={cn('flex h-[72px] items-center justify-between gap-4 border-b border-border px-4', className)}>
            <label htmlFor={id} className="cursor-pointer text-base leading-5 tracking-[0.03em] text-foreground">
                {label}
            </label>
            <Checkbox id={id} checked={checked} onCheckedChange={(next) => onCheckedChange(next === true)} />
        </div>
    );
}
